<template>
  <div :class="d - flex" :key="generateId(schema.model)">
    <v-file-input
      v-if="!isThumbnail"
      v-bind="schema.attributesTextField"
      v-model="value"
      :rules="rules"
      :name="schema.model"
      :id="generateId(schema.model)"
      :ref="schema.model"
      @blur="onBlur"
      @change="onChange"
      @focus="onFocus"
      @input="onInput"
    >
      <template v-slot:label>
        {{ schema.attributesTextField.label }}
        <span v-if="schema.rules.includes('required')" class="red--text">*</span>
      </template>

      <template v-slot:selection="{ text }" v-if="schema.attributesChips.visible">
        <v-chip v-bind="schema.attributesChips">{{ text }}</v-chip>
      </template>
    </v-file-input>

    <template v-else>
      <v-hover v-slot:default="{ hover }" v-if="document && isImages">
        <v-card :elevation="hover ? 12 : 2" v-bind="schema.attributesFile.card">
          <v-img :src="image" v-bind="schema.attributesFile.image">
            <v-row v-if="hover" align="center" justify="center" class="hover-card-image">
              <v-col cols="12">
                <v-card-text class="text-h5 font-weight-bold">{{
                  schema.attributesTextField.label || document.fileName
                }}</v-card-text>
                <v-card-text>{{ document.fileName }}</v-card-text>
                <v-card-text v-if="document.size" class="font-weight-bold">{{ document.size }}</v-card-text>

                <v-tooltip bottom v-if="!schema.attributesFile?.hideDelete">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      data-has-tooltip="true"
                      data-tooltip="Deletar Arquivo"
                      text
                      icon
                      dark
                      v-on="on"
                      @click="deleteDocument()"
                    >
                      <v-icon>delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Deletar Arquivo</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      data-has-tooltip="true"
                      data-tooltip="Baixar Arquivo"
                      text
                      icon
                      dark
                      v-on="on"
                      @click="downloadDocument()"
                    >
                      <v-icon>get_app</v-icon>
                    </v-btn>
                  </template>
                  <span>Baixar Arquivo</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-img>
        </v-card>
      </v-hover>

      <v-card v-if="document && !isImages" v-bind="schema.attributesFile.card">
        <v-card-title class="text-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon large left v-on="on">{{ document.contentType }}</v-icon>
            </template>
            <span>{{ document.fileName }}</span>
          </v-tooltip>

          <span v-if="document.size" class="text-h6 font-weight-light">{{ document.size }}</span>
        </v-card-title>

        <v-card-text class="text-h5 font-weight-bold">{{
          schema.attributesTextField.label || document.fileName
        }}</v-card-text>

        <v-card-actions>
          <v-list-item class="grow">
            <v-row align="center" justify="end">
              <v-tooltip bottom v-if="!schema.attributesFile?.hideDelete">
                <template v-slot:activator="{ on }">
                  <v-btn
                    data-has-tooltip="true"
                    data-tooltip="Deletar Arquivo"
                    text
                    icon
                    v-on="on"
                    @click="deleteDocument()"
                  >
                    <v-icon>delete</v-icon>
                  </v-btn>
                </template>
                <span>Deletar Arquivo</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    data-has-tooltip="true"
                    data-tooltip="Baixar Arquivo"
                    text
                    icon
                    v-on="on"
                    @click="downloadDocument()"
                  >
                    <v-icon>get_app</v-icon>
                  </v-btn>
                </template>
                <span>Baixar Arquivo</span>
              </v-tooltip>
            </v-row>
          </v-list-item>
        </v-card-actions>
      </v-card>
    </template>
  </div>
</template>

<script>
import { abstractField } from 'vue-form-generator';
import rulesMixin from '@/mixins/rules-mixin';
import { formatBytes, getIcon, downloadFile, isBase64File, b64toBlob } from '@/helpers/utils';
import inputEventsMixin from '@/mixins/input-events-mixin';
import validatorAcceptInputFileMixin from '@/mixins/validator-accept-input-file-mixin';
import componentMixin from '@/mixins/component-mixin';
import {
  writeImagePathToCache,
  deleteImagePathToCache,
  returnNodeDataRequest,
  executeServicesData
} from '@/helpers/page';

export default {
  mixins: [abstractField, rulesMixin, inputEventsMixin, validatorAcceptInputFileMixin, componentMixin],

  data() {
    return {
      isThumbnail: false,
      isImages: false,
      document: null,
      image: null,
      targetImageToShowThumbnail: null,
      preloadedFile: null
    };
  },

  async created() {
    this.configurationThumbnail(this.value ?? null);

    if (this.value?.name) {
      const { mappedKeys } = this.schema;
      const keyModel = this.idDynamic;

      writeImagePathToCache(
        { page: this.formOptions.page, mode: this.formOptions.mode, nameFileModel: this.value.name, keyModel },
        {
          contextId: this.hasGenerateDynamic ? this.idDynamic : null,
          contextTypeId: mappedKeys?.usingContextTable?.contextTypeId
        }
      );
    }

    if (this.schema?.executeDownloadInLoad && this.schema?.executeDownloadInLoad === true) {
      await this.preloadFile();
    }
  },

  beforeMount() {
    const imageb64InModel =
      this.isThumbnail && this.image && typeof this.value === 'string' && isBase64File(this.image);

    if (imageb64InModel && !this.hasGenerateDynamic) {
      const b64Data = this.image.split(',')[1];
      const contentType = this.model[`${this.schema.model}_content_type`];
      const fileName = this.model[`${this.schema.model}_filename`];

      this.targetImageToShowThumbnail = b64Data;

      const blob = b64toBlob(b64Data, contentType, 512);
      const file = new File([blob], fileName, { type: contentType });
      this.$nextTick(() => {
        this.value = file;
      });
    }
  },

  methods: {
    resetValue() {
      setTimeout(() => {
        this.value = null;
      }, 10);
    },
    isNewFile(value) {
      return value?.constructor?.name === 'File' && this.value;
    },
    async handleNewFileAndConvertBase64(file) {
      const base64 = await this.convertFileToBase64(file);

      this.value = {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
        lastModifiedDate: file.lastModifiedDate,
        webkitRelativePath: file.webkitRelativePath,
        base64
      };
    },

    configurationThumbnail(content) {
      const baseModel = this.schema.model;
      const hasContentByContext = this.hasGenerateDynamic && content;

      const fileName = hasContentByContext ? content.name : this.model[`${baseModel}_filename`];
      const contentType = hasContentByContext ? content.type : this.model[`${baseModel}_content_type`];
      const size = hasContentByContext ? content.size : this.model[`${baseModel}_size`];

      // Se contentType não for fornecido, extrair a extensão do filename
      if (!contentType && fileName) {
        const extension = fileName.split('.').pop();
        contentType = extension ? `${extension}` : 'application/octet-stream';
      }

      if (fileName && contentType) {
        this.isThumbnail = true;
        this.createThumbnail(fileName, contentType, size, content);
      }
    },

    createThumbnail(fileName, contentType, size, contentFile) {
      const content = this.hasGenerateDynamic ? contentFile : this.model[this.schema.model];

      this.document = {
        content,
        size: size ? formatBytes(size) : null,
        fileName,
        contentType: getIcon(fileName)
      };

      this.isImages = contentType.includes('image/');

      if (this.isImages) {
        const valueImageb64 = this.targetImageToShowThumbnail ?? this.model[this.schema.model];
        this.image = this.hasGenerateDynamic ? content.base64 : `data:${contentType};base64,${valueImageb64}`;
      }
    },

    deleteDocument() {
      const { model } = this.schema;

      this.value = [];
      this.isThumbnail = false;
      this.image = null;

      ['_filename', '_content_type', '_size', ''].forEach(suffix => {
        this.model[`${model}${suffix}`] = null;
      });

      deleteImagePathToCache(this.hasGenerateDynamic ? this.idDynamic : model);
    },

    // Novo método para pré-carregar o download do arquivo
    async preloadFile() {
      try {
        const data = await this.getFile();
        this.processAndStoreFile(data);
      } catch (error) {
        console.error('Erro ao pré-carregar o arquivo:', error);
      }
    },

    async downloadDocument() {
      if (this.schema?.executeDownloadInLoad && this.schema?.executeDownloadInLoad === true) {
        // Download do arquivo já armazenado
        downloadFile(
          this.preloadedFile.content,
          this.preloadedFile.contentType,
          this.preloadedFile.fileName.split('.').pop(),
          this.preloadedFile.fileName.split('.').shift()
        );
      } else {
        // Fallback para o comportamento original - Download do arquivo via chamada no backend
        try {
          const data = await this.getFile();
          this.processAndDownloadFile(data);
        } catch (error) {
          console.error('Erro ao baixar o documento:', error);
        }
      }
    },

    async getFile() {
      if (
        !this.schema ||
        (!('procedure' in this.schema) && !('requestApi' in this.schema) && !('component' in this.schema))
      ) {
        return;
      }

      const filters =
        'requestApi' in this.schema
          ? {
              form: this.$store.getters['page/pageFilter'](this.formOptions?.page),
              filter: this.$store.getters['page/pageFilter'](this.formOptions?.page),
              defaultFilter: this.$store.getters['page/pageFilter'](this.formOptions?.page)
            }
          : {
              form: this.model,
              filter: {},
              defaultFilter: {}
            };

      const { typeRequest, bodyRequest } = returnNodeDataRequest(
        { schema: this.schema },
        {
          ...filters,
          page: this.formOptions?.page,
          isRedirect: false,
          InvokeName: 'Download File'
        }
      );

      try {
        const { data } = await executeServicesData(typeRequest, bodyRequest);
        return data;
      } catch (error) {
        console.error('Erro ao executar serviços de dados:', error);
        throw error;
      }
    },

    findTargetObject(data) {
      let targetObject = data.values.find(obj => obj.id === this.model.id);

      if (!targetObject) {
        console.warn('Objeto com a chave correspondente não encontrado no data.values, tentando em this.model.id');
        targetObject = data.values.find(obj => Object.keys(obj).includes(this.schema.model));
      }

      if (!targetObject) {
        console.error('Objeto com a chave correspondente não encontrado');
        return null;
      }

      return targetObject;
    },

    extractFileData(targetObject) {
    const contentKey = this.schema.model;
    const fileName = targetObject[`${contentKey}_filename`];
    let contentType = targetObject[`${contentKey}_content_type`];
    const size = targetObject[`${contentKey}_size`];

    // Se contentType não for fornecido, extrair a extensão do filename
    if (!contentType && fileName) {
      const extension = fileName.split('.').pop();
      contentType = extension ? `${extension}` : 'application/octet-stream';
    }

    return {
      content: targetObject[contentKey],
      contentType,
      fileName,
      size
    };
  },

    processAndStoreFile(data) {
      if (data && data.values && data.values.length > 0) {
        const targetObject = this.findTargetObject(data);

        if (!targetObject) return;

        const fileData = this.extractFileData(targetObject);

        // Armazena o arquivo em preloadedFile como um objeto
        this.preloadedFile = fileData;

        // Converte para Blob imediatamente
        // const blob = b64toBlob(contentFile.split(',')[1], contentType);
        // this.preloadedFile.content = blob;
      } else {
        console.error('Erro ao obter os dados do arquivo para armazenamento');
      }
    },

    // Método original tratado como fallback
    processAndDownloadFile(data) {
      if (data && data.values && data.values.length > 0) {
        const targetObject = this.findTargetObject(data);

        if (!targetObject) return;

        const fileData = this.extractFileData(targetObject);

        if (!fileData || !fileData.fileName) {
          console.error('Erro ao extrair os dados do arquivo');
          return;
        }

        downloadFile(
          fileData.content,
          fileData.contentType,
          fileData.fileName.split('.').pop(),
          fileData.fileName.split('.').shift()
        );
      } else {
        console.error('Erro ao obter os dados do arquivo');
      }
    },

    async onBlur() {
      this.changeEvents();
    },

    convertFileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }
  },

  watch: {
    async value(newValue, oldValue) {
      const { model, attributesTextField, mappedKeys } = this.schema;
      const keyModel = this.hasGenerateDynamic ? this.idDynamic : model;

      if (newValue) {
        const validAccept = this.validatorAcceptInputFile(attributesTextField, newValue);

        if (!validAccept) {
          this.resetValue();
        } else {
          if (this.isNewFile(newValue) && this.hasGenerateDynamic) {
            await this.handleNewFileAndConvertBase64(newValue);
          }

          writeImagePathToCache(
            { page: this.formOptions.page, mode: this.formOptions.mode, nameFileModel: newValue.name, keyModel },
            {
              contextId: this.hasGenerateDynamic ? this.idDynamic : null,
              contextTypeId: mappedKeys?.usingContextTable?.contextTypeId
            }
          );

          if (newValue !== oldValue) {
            if (this.hasGenerateDynamic && !newValue.base64) return;

            this.configurationThumbnail(newValue);

            if (newValue === null) {
              deleteImagePathToCache(keyModel);
            }
          }
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.v-responsive__content {
  transition: 300ms;
  .hover-card-image {
    height: 100%;
    background: rgba($color: #000000, $alpha: 0.6);
    transition: 300ms;

    div {
      color: #fff;
      text-align: center;
      padding: 0 0 10px 0;
    }
  }
}
</style>
